import { useEffect, useRef } from "react";
import { useBlocker, useNavigate } from "react-router-dom";
import {
    formatBlockedNavigationPath,
    handleNavigationBlocked,
    proceedBlockedNavigation,
    resolveBlockerResetHandler,
    shouldBlockNavigation,
    type BlockedNavigationTarget,
    type NavigationBlockerState,
} from "../core/blockerState";
import { useUnsavedChangesGuardContext } from "./UnsavedChangesGuardContext";

export type UseUnsavedChangesBlockerOptions = {
    when: boolean;
};

/** Blocks in-app navigation when dirty; reuses the shared UnsavedChangesGuardProvider modal. */
export function useUnsavedChangesBlocker({ when }: UseUnsavedChangesBlockerOptions): void {
    const context = useUnsavedChangesGuardContext();
    const navigate = useNavigate();
    const blocker = useBlocker(shouldBlockNavigation(when));
    const blockerState = blocker.state as NavigationBlockerState;
    const pendingTargetRef = useRef<BlockedNavigationTarget | null>(null);
    const handledTransitionRef = useRef<string | null>(null);

    useEffect(() => {
        if (!context) return;

        context.registerBlockerReset(
            resolveBlockerResetHandler(blockerState, () => blocker.reset())
        );

        return () => {
            context.registerBlockerReset(null);
        };
    }, [context, blocker, blockerState]);

    useEffect(() => {
        if (!context || blockerState !== "blocked") {
            if (blockerState !== "blocked") {
                handledTransitionRef.current = null;
            }
            return;
        }

        const transitionKey = `${blocker.location.pathname}${blocker.location.search}${blocker.location.hash}`;
        if (handledTransitionRef.current === transitionKey) return;
        handledTransitionRef.current = transitionKey;

        pendingTargetRef.current = {
            pathname: blocker.location.pathname,
            search: blocker.location.search,
            hash: blocker.location.hash,
        };

        handleNavigationBlocked(blockerState, context.requestProceed, () => {
            proceedBlockedNavigation({
                blockerState: blocker.state as NavigationBlockerState,
                target: pendingTargetRef.current,
                proceedBlocker: () => blocker.proceed(),
                navigateToTarget: (target) => navigate(formatBlockedNavigationPath(target)),
            });
            pendingTargetRef.current = null;
        });
    }, [context, blocker, blockerState, navigate]);
}
